import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { CreateManualSong } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Modal para agregar canciones o artistas manualmente desde la Cabina DJ.
 * Cuenta con pestañas independientes para 'Sugerir Canción' y 'Sugerir Artista'.
 * 
 * @component PlaylistManualSongModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla la visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} props.onSuccess - Callback al crear satisfactoriamente la canción/artista.
 */
export const PlaylistManualSongModal = ({ isOpen, onClose, onSuccess }) => {
    const { showMessage } = useFloatMessage();

    const [activeTab, setActiveTab] = useState('song'); // 'song' | 'artist'
    const [songTitle, setSongTitle] = useState('');
    const [artistName, setArtistName] = useState('');
    const [operatorName, setOperatorName] = useState('Cabina DJ');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Resetear formulario al abrir/cerrar
    useEffect(() => {
        if (isOpen) {
            setSongTitle('');
            setArtistName('');
            setOperatorName('Cabina DJ');
            setActiveTab('song');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    // Cerrar con Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && isOpen) {
            onClose();
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanSongTitle = (songTitle || '').trim();
        const cleanArtistName = (artistName || '').trim();
        const cleanOperator = (operatorName || '').trim() || 'Cabina DJ';

        if (activeTab === 'song') {
            if (!cleanSongTitle) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'El título de la canción es obligatorio.',
                    message: 'El título de la canción es obligatorio.'
                });
                return;
            }
        } else {
            if (!cleanArtistName) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'El nombre del artista es obligatorio.',
                    message: 'El nombre del artista es obligatorio.'
                });
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const payload = {
                suggestionType: activeTab === 'song' ? 1 : 2,
                title: activeTab === 'song' ? cleanSongTitle : cleanArtistName,
                artistName: activeTab === 'song' ? (cleanArtistName || null) : (cleanSongTitle || null),
                operatorName: cleanOperator
            };

            const result = await CreateManualSong(payload);

            if (result && result.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: activeTab === 'song'
                        ? `¡Canción "${cleanSongTitle}" agregada a la cabina!`
                        : `¡Artista "${cleanArtistName}" agregado a la cabina!`,
                    message: activeTab === 'song'
                        ? `¡Canción "${cleanSongTitle}" agregada a la cabina!`
                        : `¡Artista "${cleanArtistName}" agregado a la cabina!`
                });

                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess(result.data);
                }
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (result && result.message) || 'Error al agregar la canción manual.',
                    message: (result && result.message) || 'Error al agregar la canción manual.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al guardar el registro manual.',
                message: 'Error de conexión al guardar el registro manual.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="PlaylistModal__Overlay" role="dialog" aria-modal="true">
            <div className="PlaylistModal__Backdrop" onClick={onClose} />
            <div className="PlaylistModal__Dialog">
                <div className="PlaylistModal__Content">
                    {/* Header */}
                    <div className="PlaylistModal__Header">
                        <div className="d-flex align-items-center gap-2">
                            <span className="PlaylistModal__HeaderIcon">
                                <i className="bi bi-plus-circle-fill text-danger"></i>
                            </span>
                            <div>
                                <h4 className="PlaylistModal__Title mb-0">Agregar Canción / Artista Manual</h4>
                                <p className="PlaylistModal__Subtitle mb-0">Ingreso directo a moderación desde la cabina DJ</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="PlaylistModal__CloseButton"
                            onClick={onClose}
                            aria-label="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Nav Tabs */}
                    <div className="PlaylistModal__Tabs">
                        <button
                            type="button"
                            className={`PlaylistModal__Tab ${activeTab === 'song' ? 'PlaylistModal__Tab--active' : ''}`}
                            onClick={() => setActiveTab('song')}
                        >
                            <i className="bi bi-music-note-beamed me-2"></i>
                            Sugerir Canción
                        </button>
                        <button
                            type="button"
                            className={`PlaylistModal__Tab ${activeTab === 'artist' ? 'PlaylistModal__Tab--active' : ''}`}
                            onClick={() => setActiveTab('artist')}
                        >
                            <i className="bi bi-person-lines-fill me-2"></i>
                            Sugerir Artista
                        </button>
                    </div>

                    {/* Body Form */}
                    <form onSubmit={handleSubmit}>
                        <div className="PlaylistModal__Body">
                            {activeTab === 'song' ? (
                                <>
                                    <div className="PlaylistModal__Field mb-3">
                                        <label className="PlaylistModal__Label" htmlFor="manual-song-title">
                                            Título de la Canción <span className="text-danger">*</span>
                                        </label>
                                        <div className="PlaylistModal__InputWrapper">
                                            <i className="bi bi-music-note PlaylistModal__InputIcon"></i>
                                            <input
                                                id="manual-song-title"
                                                type="text"
                                                className="PlaylistModal__Input"
                                                placeholder="Ej. Matador, Thunderstruck, La Chona..."
                                                value={songTitle}
                                                onChange={(e) => setSongTitle(e.target.value)}
                                                maxLength={100}
                                                required
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    <div className="PlaylistModal__Field mb-3">
                                        <label className="PlaylistModal__Label" htmlFor="manual-song-artist">
                                            Artista / Banda (Opcional)
                                        </label>
                                        <div className="PlaylistModal__InputWrapper">
                                            <i className="bi bi-mic PlaylistModal__InputIcon"></i>
                                            <input
                                                id="manual-song-artist"
                                                type="text"
                                                className="PlaylistModal__Input"
                                                placeholder="Ej. Los Fabulosos Cadillacs, AC/DC, Los Tucanes..."
                                                value={artistName}
                                                onChange={(e) => setArtistName(e.target.value)}
                                                maxLength={80}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="PlaylistModal__Field mb-3">
                                        <label className="PlaylistModal__Label" htmlFor="manual-artist-name">
                                            Nombre del Artista / Banda <span className="text-danger">*</span>
                                        </label>
                                        <div className="PlaylistModal__InputWrapper">
                                            <i className="bi bi-person-badge PlaylistModal__InputIcon"></i>
                                            <input
                                                id="manual-artist-name"
                                                type="text"
                                                className="PlaylistModal__Input"
                                                placeholder="Ej. Queen, Bad Bunny, Metallica, Banda MS..."
                                                value={artistName}
                                                onChange={(e) => setArtistName(e.target.value)}
                                                maxLength={80}
                                                required
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    <div className="PlaylistModal__Field mb-3">
                                        <label className="PlaylistModal__Label" htmlFor="manual-artist-song">
                                            Canción Recomendada / Comentario (Opcional)
                                        </label>
                                        <div className="PlaylistModal__InputWrapper">
                                            <i className="bi bi-disc PlaylistModal__InputIcon"></i>
                                            <input
                                                id="manual-artist-song"
                                                type="text"
                                                className="PlaylistModal__Input"
                                                placeholder="Ej. Cualquier éxito / Canción representativa"
                                                value={songTitle}
                                                onChange={(e) => setSongTitle(e.target.value)}
                                                maxLength={100}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="PlaylistModal__Field mb-1">
                                <label className="PlaylistModal__Label" htmlFor="manual-operator-name">
                                    Operador / DJ Responsable
                                </label>
                                <div className="PlaylistModal__InputWrapper">
                                    <i className="bi bi-headset PlaylistModal__InputIcon"></i>
                                    <input
                                        id="manual-operator-name"
                                        type="text"
                                        className="PlaylistModal__Input"
                                        placeholder="Cabina DJ"
                                        value={operatorName}
                                        onChange={(e) => setOperatorName(e.target.value)}
                                        maxLength={100}
                                    />
                                </div>
                                <span className="PlaylistModal__HelpText">
                                    Esta pista se identificará con la etiqueta <strong>Manual</strong> en moderación.
                                </span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="PlaylistModal__Footer">
                            <button
                                type="button"
                                className="PlaylistModal__Btn PlaylistModal__Btn--secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="PlaylistModal__Btn PlaylistModal__Btn--primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        Guardar en Cabina
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PlaylistManualSongModal;
